/**
 * @file 消息卡片
 * @author alfielv
 */

import React, { useEffect, useState } from 'react';
import { Frame, Text, Flex } from '@tencent/odc';
import classNames from 'classnames';

import { ATTENTION_EVENT_INFO } from './constants';
import type { KeyEventItem } from './types';
import reportHub from '@/common/report-hub';

export interface MessageCardProps {
  /**
   * 数据
   */
  data: KeyEventItem;
  /**
   * 自定义类名
   */
  className?: string;
  /**
  * 处理事件点击
   */
  handleAction: (event_type: number, account_id_list: number[]) => void;
}

const typeTheme = {
  error: {
    icon:
      'https://qzonestyle.gdtimg.com/gdt_ui_proj/imghub/dist/17410145195.svg?max_age=31536000',
    dotColor: 'var(--color-Red-06, #F00)',
    bg: '#FFF8F7',
    hoverBg: '#FFE6E4',
    // textColor: 'var(---Red-08, #BF0000)',
  },
  warning: {
    icon:
      'https://qzonestyle.gdtimg.com/gdt_ui_proj/imghub/dist/1732756676781.svg?max_age=31536000',
    dotColor: 'var(---color-warn, #FF7E00)',
    bg: '#FFF8EE',
    hoverBg: '#FFF2E1',
    // textColor: 'var(---Orange-09, #C15600)',
  },
  info: {
    icon:
      'https://qzonestyle.gdtimg.com/gdt_ui_proj/imghub/dist/1732756836083.svg?max_age=31536000',
    dotColor: 'var(---color-link, #006FFF)',
    bg: '#F3F8FF',
    hoverBg: '#EAF3FF',
    // textColor: 'var(---Blue-09, #0038A6)',
  },
};

const MessageCard = ({
  data,
  className,
  handleAction,
}: MessageCardProps) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { event_type, account_id_list = [] } = data;

  const eventInfo = ATTENTION_EVENT_INFO[event_type] || {};
  const { title, desc: description, getType, actionText = '查看详情', reportClickActionName, reportDisplayActionName } = eventInfo;
  const type = getType?.(data);

  const [isHovered, setIsHovered] = useState(false);

  // 手动上报卡片曝光量
  useEffect(() => {
    reportHub.reportAction({
      action: reportDisplayActionName,
    });
  }, []);

  return (
    <Flex
      className={classNames('cursor-pointer tw-px-[12px] tw-py-[12px] tw-group/item tw-overflow-hidden', className)}
      radius={8}
      style={{
        transition: 'all 0.1s ease-in-out',
        background: isHovered ? typeTheme[type]?.hoverBg : typeTheme[type]?.bg,
      }}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      onClick={() => handleAction(event_type, account_id_list)}
      data-report={reportClickActionName}
    >
      <Flex gap={6} w="100%">
        <Flex h={22} itemsCenter>
          <Frame w={6} h={6} radius="full" fill={typeTheme[type]?.dotColor} />
        </Flex>
        <Flex column gap={2} className='flex-1 tw-overflow-hidden'>
          <Text
            as="strong"
            lineClamp={1}
            usePopOver
            className="tw-text-[#262729]"
            block
            title={title?.(data)}
            nowrap
          >
            {title?.(data)}
          </Text>
          <Text
            className='flex-1'
            lineClamp={1}
            usePopOver
            color="var(---color-secondary_text, rgba(38, 38, 41, 0.72))"
            size="small"
            block
            title={description}
            nowrap
          >
            {description}
          </Text>
          {actionText && (
            <Flex className='tw-mt-[10px]'>
              <Text label={actionText} type="link" size="small" />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default MessageCard;
